import { useRef, useState, useEffect } from "react";
import { exportToJSON, importFromJSON } from "../utils/storage";

export default function ImportExport({ onImport, onExportJson, onExportSvg, onPrint }) {
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleExportJson = () => {
    setMenuOpen(false);
    if (onExportJson) {
      const data = onExportJson();
      exportToJSON(data);
    }
  };

  const handleExportSvg = () => {
    setMenuOpen(false);
    onExportSvg?.();
  };

  const handlePrint = () => {
    setMenuOpen(false);
    onPrint?.();
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await importFromJSON(file);
      onImport(data);
    } catch (err) {
      alert("Failed to import file: " + err.message);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <button className="toolbar-btn" onClick={handleImportClick} title="Import JSON">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <polyline points="9 15 12 18 15 15" />
        </svg>
        <span className="btn-label">Import</span>
      </button>

      <div className="export-menu" ref={menuRef}>
        <button
          className={`toolbar-btn${menuOpen ? " toolbar-btn-active" : ""}`}
          onClick={() => setMenuOpen((v) => !v)}
          title="Export"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="11" x2="12" y2="17" />
            <polyline points="9 14 12 11 15 14" />
          </svg>
          <span className="btn-label">Export</span>
          <svg className="export-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {menuOpen && (
          <div className="export-dropdown" role="menu">
            <button type="button" className="export-dropdown-item" role="menuitem" onClick={handleExportJson}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" />
                <path d="M16 3h1a2 2 0 0 1 2 2v5a2 2 0 0 0 2 2 2 2 0 0 0-2 2v5a2 2 0 0 1-2 2h-1" />
              </svg>
              <span>
                <strong>JSON</strong>
                <small>Full data backup</small>
              </span>
            </button>

            <button type="button" className="export-dropdown-item" role="menuitem" onClick={handleExportSvg}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="15.5" cy="8" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="15.5" cy="16" r="1.5" fill="currentColor" stroke="none" />
                <path d="M8.5 12h7" />
                <path d="M15.5 8v8" strokeDasharray="2 2" />
              </svg>
              <span>
                <strong>SVG</strong>
                <small>Vector of this timeline</small>
              </span>
            </button>

            <button type="button" className="export-dropdown-item" role="menuitem" onClick={handlePrint}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              <span>
                <strong>Print</strong>
                <small>Print this timeline</small>
              </span>
            </button>
          </div>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleFileChange} />
    </>
  );
}
